import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { LogOut, Building2, Check, Loader as Loader2, Pencil, X, LayoutGrid, Globe, MapPin, FileText, ChevronDown, ChevronUp, Package, Truck, ClipboardList } from 'lucide-react';
import { Quotation, STATUS_COLORS, STATUS_OPTIONS, formatQuotationDate } from '@/lib/quotation-types';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
];

interface AddressState {
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_country: string;
}

interface QuotationItemProps {
  q: Quotation;
  onStatusChange: (id: string, status: string) => Promise<void>;
}

function QuotationItem({ q, onStatusChange }: QuotationItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const bom = q.bom_snapshot;

  const handleStatus = async (status: string) => {
    setUpdatingStatus(true);
    await onStatusChange(q.id, status);
    setUpdatingStatus(false);
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{q.company}</span>
            <span className="text-xs text-slate-500">{q.contact_name}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-slate-500">{formatQuotationDate(q.created_at)}</span>
            {bom && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                €{bom.totalPrice.toFixed(2)}
              </span>
            )}
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[q.status] ?? 'bg-slate-100 text-slate-600'}`}>
              {q.status}
            </span>
            {q.configuration && (
              <span className="text-xs text-slate-400">
                {q.configuration.model} · {q.configuration.height}×{q.configuration.width}×{q.configuration.depth} cm
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-slate-400 mt-0.5">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Delivery details</p>
              </div>
              <div className="text-sm text-slate-700 space-y-0.5">
                {q.address && <p>{q.address}</p>}
                <p>{[q.postal_code, q.city].filter(Boolean).join(' ')}</p>
                <p>{q.country}</p>
                {q.phone && <p className="text-slate-500">{q.phone}</p>}
              </div>
              <div className="flex items-center gap-1.5">
                <Truck className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-600 capitalize">{q.shipping_method}</span>
              </div>
              {q.notes && (
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 italic">
                  {q.notes}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</p>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s}
                    disabled={updatingStatus || q.status === s}
                    onClick={e => { e.stopPropagation(); handleStatus(s); }}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors ${
                      q.status === s
                        ? STATUS_COLORS[s]
                        : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-400'
                    } disabled:opacity-50`}
                  >
                    {s}
                  </button>
                ))}
                {updatingStatus && <Loader2 className="h-4 w-4 animate-spin text-slate-400 ml-1" />}
              </div>
            </div>
          </div>

          {bom && bom.items.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  BOM — {bom.totalWeight} kg · €{bom.totalPrice.toFixed(2)}
                </p>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium text-slate-600">Description</th>
                      <th className="text-right px-2 py-1.5 font-medium text-slate-600">Qty</th>
                      <th className="text-right px-2 py-1.5 font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {bom.items.map((item, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1.5 text-slate-700">{item.description}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{item.quantity}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-slate-800">€{item.totalPrice.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr>
                      <td colSpan={2} className="px-2 py-1.5 text-right font-bold text-slate-700">Total:</td>
                      <td className="px-2 py-1.5 text-right font-bold text-green-700">€{bom.totalPrice.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ResellerPanel() {
  const { user, reseller, signOut, refreshProfile } = useAuthStore();
  const navigate = useNavigate();

  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [quotationsLoading, setQuotationsLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [margin, setMargin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingLang, setEditingLang] = useState(false);
  const [langValue, setLangValue] = useState('');
  const [savingLang, setSavingLang] = useState(false);

  const [editingAddress, setEditingAddress] = useState(false);
  const [addressState, setAddressState] = useState<AddressState>({ address_street: '', address_city: '', address_postal_code: '', address_country: '' });
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  const [editingColor, setEditingColor] = useState(false);
  const [colorValue, setColorValue] = useState('');
  const [savingColor, setSavingColor] = useState(false);

  const fetchQuotations = async (resellerId: string) => {
    setQuotationsLoading(true);
    const { data } = await supabase
      .from('quotation_requests')
      .select('*')
      .eq('reseller_id', resellerId)
      .order('created_at', { ascending: false });
    setQuotations(data ?? []);
    setQuotationsLoading(false);
  };

  useEffect(() => {
    if (!reseller) { setQuotationsLoading(false); return; }
    fetchQuotations(reseller.id);
  }, [reseller?.id]);

  const handleStatusChange = async (id: string, status: string) => {
    await supabase.from('quotation_requests').update({ status }).eq('id', id);
    if (reseller) await fetchQuotations(reseller.id);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const startEdit = () => {
    setMargin(String(reseller?.margin_percent ?? 0));
    setEditing(true);
    setError(null);
  };

  const cancelEdit = () => { setEditing(false); setError(null); };

  const saveMargin = async () => {
    if (!reseller) return;
    setSaving(true);
    setError(null);
    const { error } = await supabase
      .from('resellers')
      .update({ margin_percent: parseFloat(margin) || 0 })
      .eq('id', reseller.id);
    setSaving(false);
    if (error) { setError(error.message); return; }
    setEditing(false);
    await refreshProfile();
  };

  const startEditLang = () => {
    setLangValue(reseller?.default_language ?? 'en');
    setEditingLang(true);
  };

  const cancelEditLang = () => setEditingLang(false);

  const saveLang = async () => {
    if (!reseller) return;
    setSavingLang(true);
    const { error } = await supabase
      .from('resellers')
      .update({ default_language: langValue })
      .eq('id', reseller.id);
    setSavingLang(false);
    if (error) return;
    setEditingLang(false);
    await refreshProfile();
  };

  const startEditAddress = () => {
    setAddressState({
      address_street: reseller?.address_street ?? '',
      address_city: reseller?.address_city ?? '',
      address_postal_code: reseller?.address_postal_code ?? '',
      address_country: reseller?.address_country ?? '',
    });
    setEditingAddress(true);
    setAddressError(null);
  };

  const cancelEditAddress = () => { setEditingAddress(false); setAddressError(null); };

  const saveAddress = async () => {
    if (!reseller) return;
    setSavingAddress(true);
    setAddressError(null);
    const { error } = await supabase
      .from('resellers')
      .update(addressState)
      .eq('id', reseller.id);
    setSavingAddress(false);
    if (error) { setAddressError(error.message); return; }
    setEditingAddress(false);
    await refreshProfile();
  };

  const startEditColor = () => {
    setColorValue(reseller?.brand_color ?? '#212b4f');
    setEditingColor(true);
  };

  const cancelEditColor = () => setEditingColor(false);

  const saveColor = async () => {
    if (!reseller) return;
    setSavingColor(true);
    const { error } = await supabase
      .from('resellers')
      .update({ brand_color: colorValue.trim() || null })
      .eq('id', reseller.id);
    setSavingColor(false);
    if (error) return;
    setEditingColor(false);
    await refreshProfile();
  };

  const currentLangLabel = LANGUAGE_OPTIONS.find(l => l.value === (reseller?.default_language ?? 'en'))?.label ?? 'English';

  const hasAddress = reseller && (
    reseller.address_street || reseller.address_city || reseller.address_postal_code || reseller.address_country
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#212b4f] text-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-300" />
            <span className="font-semibold tracking-tight">Reseller Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-300 text-sm">{user?.email}</span>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white hover:bg-white/10 h-8 gap-1.5"
            >
              <LayoutGrid className="h-4 w-4" />
              Configurator
            </Button>
            <Button
              onClick={handleSignOut}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white hover:bg-white/10 h-8 gap-1.5"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {!reseller ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-center text-slate-500">
            <Building2 className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No reseller linked</p>
            <p className="text-sm mt-1">Contact an administrator to link your account to a reseller.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">{reseller.name}</h2>
              <p className="text-sm text-slate-500 mt-0.5">Your reseller account</p>
            </div>

            {error && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{error}</div>
            )}

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Discount</p>
                  <p className="text-2xl font-bold text-[#212b4f]">{reseller.discount_percent}%</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Your Margin</p>
                      {editing ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={margin}
                            onChange={e => setMargin(e.target.value)}
                            className="w-24 px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      ) : (
                        <p className="text-2xl font-bold text-[#212b4f]">{reseller.margin_percent}%</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {editing ? (
                        <>
                          <button
                            onClick={saveMargin}
                            disabled={saving}
                            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button onClick={cancelEdit} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button onClick={startEdit} className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      <Globe className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Default Language</p>
                        {editingLang ? (
                          <select
                            value={langValue}
                            onChange={e => setLangValue(e.target.value)}
                            className="mt-1 px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] bg-white"
                          >
                            {LANGUAGE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-base font-semibold text-[#212b4f]">{currentLangLabel}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {editingLang ? (
                        <>
                          <button
                            onClick={saveLang}
                            disabled={savingLang}
                            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                          >
                            {savingLang ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button onClick={cancelEditLang} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button onClick={startEditLang} className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <div className="flex items-start gap-2">
                  <Globe className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Domain</p>
                      {reseller.domain ? (
                        <p className="text-base font-semibold text-[#212b4f]">{reseller.domain}</p>
                      ) : (
                        <p className="text-sm text-slate-400">Not configured</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Shop URL</p>
                      {reseller.shop_url ? (
                        <p className="text-sm font-semibold text-[#212b4f] break-all">{reseller.shop_url}</p>
                      ) : (
                        <p className="text-sm text-slate-400">Not configured</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <div
                      className="h-4 w-4 rounded-full border border-slate-300 shrink-0 mt-0.5"
                      style={{ backgroundColor: reseller.brand_color ?? '#212b4f' }}
                    />
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Brand Color</p>
                      {editingColor ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={colorValue}
                            onChange={e => setColorValue(e.target.value)}
                            className="h-8 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
                          />
                          <input
                            type="text"
                            value={colorValue}
                            onChange={e => setColorValue(e.target.value)}
                            className="w-28 px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f] font-mono"
                            placeholder="#212b4f"
                          />
                        </div>
                      ) : (
                        <p className="text-base font-semibold font-mono" style={{ color: reseller.brand_color ?? '#212b4f' }}>
                          {reseller.brand_color ?? '#212b4f'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {editingColor ? (
                      <>
                        <button
                          onClick={saveColor}
                          disabled={savingColor}
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                        >
                          {savingColor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEditColor} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <button onClick={startEditColor} className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <MapPin className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Company Address</p>
                      {editingAddress ? (
                        <div className="space-y-2 mt-1">
                          {addressError && (
                            <p className="text-xs text-red-600">{addressError}</p>
                          )}
                          <input
                            type="text"
                            value={addressState.address_street}
                            onChange={e => setAddressState(s => ({ ...s, address_street: e.target.value }))}
                            placeholder="Street and number"
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={addressState.address_postal_code}
                              onChange={e => setAddressState(s => ({ ...s, address_postal_code: e.target.value }))}
                              placeholder="Postal code"
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                            />
                            <input
                              type="text"
                              value={addressState.address_city}
                              onChange={e => setAddressState(s => ({ ...s, address_city: e.target.value }))}
                              placeholder="City"
                              className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                            />
                          </div>
                          <input
                            type="text"
                            value={addressState.address_country}
                            onChange={e => setAddressState(s => ({ ...s, address_country: e.target.value }))}
                            placeholder="Country"
                            className="w-full px-2 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#212b4f]"
                          />
                        </div>
                      ) : hasAddress ? (
                        <div className="text-sm text-slate-700 space-y-0.5 mt-0.5">
                          {reseller.address_street && <p>{reseller.address_street}</p>}
                          {(reseller.address_postal_code || reseller.address_city) && (
                            <p>{[reseller.address_postal_code, reseller.address_city].filter(Boolean).join(' ')}</p>
                          )}
                          {reseller.address_country && <p>{reseller.address_country}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 mt-0.5">No address on file</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2 shrink-0">
                    {editingAddress ? (
                      <>
                        <button
                          onClick={saveAddress}
                          disabled={savingAddress}
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                        >
                          {savingAddress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEditAddress} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <button onClick={startEditAddress} className="p-1.5 rounded-lg text-slate-400 hover:text-[#212b4f] hover:bg-slate-100 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {reseller && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              <h2 className="font-semibold text-slate-800">Quotation Requests</h2>
              {!quotationsLoading && (
                <span className="ml-1 text-sm text-slate-400 font-normal">({quotations.length})</span>
              )}
            </div>

            <div className="px-6 py-4">
              {quotationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
                </div>
              ) : quotations.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Package className="h-6 w-6 mx-auto mb-2 text-slate-200" />
                  <p className="text-sm">No quotation requests yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {quotations.map(q => (
                    <QuotationItem key={q.id} q={q} onStatusChange={handleStatusChange} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
